import { Store } from '@nextforce/api'
import { Button, Text, makeStyles } from '@rneui/themed'
import { t } from 'app/i18l'
import { useCallback } from 'react'
import { Pressable, View } from 'react-native'
import { FlatList } from 'react-native-gesture-handler'
import { SafeAreaView } from 'react-native-safe-area-context'
import { CheckBox } from 'shared/components/check_box'
import { ListEmptyComponent } from 'shared/components/empty_list'

import SearchBar from 'shared/components/search_bar'

interface Props {
  stores: Store[]
  searchTerm: string
  setSearchTerm: React.Dispatch<React.SetStateAction<string>>
  selectedStoreIds: number[]
  setSelectedStoreIds(selectedStoreIds: number[]): void
}
export function ObserverStoreChooserView({
  stores,
  searchTerm,
  setSearchTerm,
  selectedStoreIds,
  setSelectedStoreIds,
}: Props) {
  const styles = useStyles()
  const onPressStoreListItem = useCallback(
    (id: number) => {
      if (selectedStoreIds.includes(id)) {
        setSelectedStoreIds(selectedStoreIds.filter(storeId => storeId !== id))
      } else {
        setSelectedStoreIds([...selectedStoreIds, id])
      }
    },
    [selectedStoreIds, setSelectedStoreIds]
  )

  const onPressSelectAll = useCallback(() => {
    setSelectedStoreIds(stores.map(store => Number(store.id)))
  }, [stores, setSelectedStoreIds])

  const onPressClearAll = useCallback(() => {
    setSelectedStoreIds([])
  }, [setSelectedStoreIds])

  return (
    <SafeAreaView edges={['left', 'right', 'bottom']} style={styles.root}>
      <View style={styles.container}>
        <FlatList
          data={stores}
          keyboardShouldPersistTaps={'handled'}
          ListEmptyComponent={<ListEmptyComponent title={t('noStoresFound')} />}
          renderItem={item => (
            <StoreListItem
              store={item.item}
              checked={selectedStoreIds.includes(Number(item.item.id))}
              onPressStoreListItem={onPressStoreListItem}
            />
          )}
          ListHeaderComponent={
            <StoreChooserSearchBar
              setSearchTerm={setSearchTerm}
              selectedStoresCount={selectedStoreIds.length}
              searchTerm={searchTerm}
              onPressClearAll={
                selectedStoreIds.length > 0 ? onPressClearAll : undefined
              }
              onPressSelectAll={
                selectedStoreIds.length < stores.length
                  ? onPressSelectAll
                  : undefined
              }
            />
          }
          stickyHeaderIndices={[0]}
        />
      </View>
    </SafeAreaView>
  )
}

function StoreChooserSearchBar({
  selectedStoresCount,
  setSearchTerm,
  searchTerm,
  onPressClearAll,
  onPressSelectAll,
}: {
  selectedStoresCount?: number
  searchTerm: string
  setSearchTerm: React.Dispatch<React.SetStateAction<string>>
  onPressSelectAll?(): void
  onPressClearAll?(): void
}) {
  const styles = useStyles()
  return (
    <View style={styles.searchBar}>
      <View style={styles.selectedStoresRowContainer}>
        <Text style={styles.selectedStoresText}>{t('selectedStores')}</Text>
        <View style={styles.storeCountContainer}>
          <Text style={styles.filteredStoreCountText}>
            {selectedStoresCount}
          </Text>
        </View>
        <View style={styles.buttonsRow}>
          <Button
            buttonStyle={styles.buttonContainer}
            disabled={!onPressSelectAll}
            onPress={onPressSelectAll}
            title={t('all')}
            type="outline"
          />
          <Button
            buttonStyle={styles.buttonContainer}
            disabled={!onPressClearAll}
            onPress={onPressClearAll}
            title={t('none')}
            type="outline"
          />
        </View>
      </View>
      <SearchBar
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        onPressClear={() => {
          setSearchTerm('')
        }}
      />
    </View>
  )
}

export function StoreListItem({
  store,
  checked,
  onPressStoreListItem,
}: {
  store: Store
  checked: boolean
  onPressStoreListItem?(id: number): void
}) {
  const styles = useStyles()
  return (
    <Pressable
      onPress={() =>
        onPressStoreListItem && onPressStoreListItem(Number(store.id))
      }
    >
      <View style={styles.storeItem}>
        <View>
          <Text style={styles.storeItemName}>
            {store.storeNumber} {store.name}
          </Text>
          <Text>{store.address}</Text>
        </View>
        <CheckBox
          checked={checked}
          checkedColor={styles.checkbox.color}
          onPress={() =>
            onPressStoreListItem && onPressStoreListItem(Number(store.id))
          }
        />
      </View>
    </Pressable>
  )
}

const useStyles = makeStyles(theme => ({
  root: {
    flex: 1,
  },
  container: { flex: 1 },
  buttonContainer: {
    borderRadius: 25,
    width: 70,
    height: 35,
    marginLeft: 5,
    padding: 0,
    borderWidth: 1,
  },
  checkbox: { color: theme.colors.primary },
  storeItem: {
    flexDirection: 'row',
    backgroundColor: theme.colors.white,
    marginTop: 1,
    padding: 10,
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  storeItemName: {
    fontSize: 16,
  },
  text: {
    marginTop: 2,
    fontSize: 16,
  },
  storeCountContainer: {
    margin: 5,
    padding: 5,
    backgroundColor: theme.colors.peach,
    borderRadius: 50,
  },
  filteredStoreCountText: {
    fontSize: 14,
    paddingHorizontal: 10,
    color: 'black',
  },
  searchBar: {
    backgroundColor: theme.colors.white,
    paddingHorizontal: 10,
    paddingBottom: 5,
  },
  selectedStoresRowContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  buttonsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flex: 1,
    marginVertical: 5,
  },
  selectedStoresText: { fontSize: 18 },
}))
